/**
 * The MIT License (MIT)
 * https://github.com/aurelia/dependency-injection
 *
 * Copyright (c) 2010 - 2018 Blue Spire Inc.
 */
import 'reflect-metadata';
import { AggregateError } from '../AggregateError';
import constants from '../constants';
import { containerResolver as resolverDeco } from '../protocol/resolver';
import { Strategy, StrategyResolver } from '../resolvers/StrategyResolver';
import { isStrategyResolver, } from '../types';
import { getInjectDependencies } from './getInjectDependencies';
import { InvocationHandler } from './InvocationHandler';
import { _emptyParameters, validateKey } from './validateParameters';
// tslint:disable:max-line-length
function invokeWithDynamicDependencies(container, fn, staticDependencies, dynamicDependencies) {
    let i = staticDependencies.length;
    let args = new Array(i);
    let lookup;
    while (i--) {
        lookup = staticDependencies[i];
        switch (lookup) {
            case null:
            case undefined:
                throw new Error(`Constructor ${fn.name} Parameter with index ${i} cannot be null or undefined. Are you trying to inject/register something that doesn't exist with DI?`);
            default:
                args[i] = container.get(lookup);
        }
    }
    if (dynamicDependencies !== undefined) {
        args = args.concat(dynamicDependencies);
    }
    return Reflect.construct(fn, args);
}
// tslint:disable:no-magic-numbers
const classInvokers = {
    [0]: {
        invoke(container, type, deps) {
            return new type();
        },
        invokeWithDynamicDependencies,
    },
    [1]: {
        invoke(container, type, deps) {
            return new type(container.get(deps[0]));
        },
        invokeWithDynamicDependencies,
    },
    [2]: {
        invoke(container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]));
        },
        invokeWithDynamicDependencies,
    },
    [3]: {
        invoke(container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]));
        },
        invokeWithDynamicDependencies,
    },
    [4]: {
        invoke(container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]), container.get(deps[3]));
        },
        invokeWithDynamicDependencies,
    },
    [5]: {
        invoke(container, type, deps) {
            return new type(container.get(deps[0]), container.get(deps[1]), container.get(deps[2]), container.get(deps[3]), container.get(deps[4]));
        },
        invokeWithDynamicDependencies,
    },
    length: 6,
    fallback: {
        invoke: invokeWithDynamicDependencies,
        invokeWithDynamicDependencies,
    },
};
// tslint:enable:no-magic-numbers
let containerNumber = 0;
/**
 * A lightweight, extensible dependency injection container.
 */
export class Container {
    get name() {
        return this._configuration.name;
    }
    /**
     * Creates an instance of Container.
     * @param configuration Provides some configuration for the new Container instance.
     */
    constructor(configuration) {
        if (configuration === undefined) {
            // tslint:disable-next-line:no-parameter-reassignment
            configuration = { name: 'root' };
        }
        containerNumber++;
        this._configuration = configuration;
        this._onHandlerCreated = configuration.onHandlerCreated;
        this._onRegisterResolver = configuration.onRegisterResolver;
        this._handlers = configuration.handlers || (configuration.handlers = new Map());
        this._resolvers = new Map();
        this.root = this;
        this.parent = undefined;
    }
    /**
     * Makes this container instance globally reachable through Container.instance.
     */
    makeGlobal() {
        Container.instance = this;
        return this;
    }
    /**
     * Sets an invocation handler creation callback that will be called when new InvocationsHandlers
     *  are created (called once per Function).
     * @param onHandlerCreated The callback to be called when an InvocationsHandler is created.
     */
    setHandlerCreatedCallback(onHandlerCreated) {
        this._onHandlerCreated = onHandlerCreated;
        this._configuration.onHandlerCreated = onHandlerCreated;
    }
    /**
     * @param onRegisterResolver The callback to be called when a Resolver is created.
     */
    setRegisterResolver(onRegisterResolver) {
        this._onRegisterResolver = onRegisterResolver;
        this._configuration.onRegisterResolver = onRegisterResolver;
    }
    /**
     * Registers an existing object instance with the container.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param instance The instance that will be resolved when the key is matched. This defaults to the key
     *                  value when instance is not supplied.
     * @return The resolver that was registered.
     */
    registerInstance(key, instance) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Instance, instance === undefined ? key : instance));
    }
    registerSingleton(key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Singleton, fn === undefined ? key : fn));
    }
    registerScoped(key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Scoped, fn === undefined ? key : fn));
    }
    registerTransient(key, fn) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Transient, fn === undefined ? key : fn));
    }
    /**
     * Registers a custom resolution function such that the container calls this function for each request
     *      to obtain the instance.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param handler The resolution function to use when the dependency is needed.
     * @return The resolver that was registered.
     */
    registerHandler(key, handler) {
        return this.registerResolver(key, new StrategyResolver(Strategy.Function, handler));
    }
    /**
     * Registers an additional key that serves as an alias to the original DI key.
     * @param originalKey The key that originally identified the dependency; usually a constructor function.
     * @param aliasKey An alternate key which can also be used to resolve the same dependency  as the original.
     * @return The resolver that was registered.
     */
    registerAlias(originalKey, aliasKey) {
        return this.registerResolver(aliasKey, new StrategyResolver(Strategy.Alias, originalKey));
    }
    /**
     * Registers a custom resolution function such that the container calls this function for each request
     *              to obtain the instance.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param resolver The resolver to use when the dependency is needed.
     * @return The resolver that was registered.
     */
    registerResolver(key, resolver) {
        validateKey(key);
        resolver =
            this._onRegisterResolver != null ? this._onRegisterResolver(key, resolver) : resolver;
        const allResolvers = this._resolvers;
        const result = allResolvers.get(key);
        if (result === undefined) {
            allResolvers.set(key, resolver);
        }
        else if (result instanceof StrategyResolver && result.strategy === Strategy.Array) {
            result.state.unshift(resolver);
        }
        else {
            allResolvers.set(key, new StrategyResolver(Strategy.Array, [resolver, result]));
        }
        return resolver;
    }
    autoRegister(key, fn) {
        // tslint:disable-next-line:no-parameter-reassignment
        fn = fn === undefined ? key : fn;
        if (typeof fn === 'function') {
            const registration = Reflect.getMetadata(constants.registration, fn);
            if (registration === undefined) {
                return this.registerResolver(key, new StrategyResolver(Strategy.Singleton, fn));
            }
            return registration.registerResolver(this, key, fn);
        }
        throw new Error(`Cannot auto register a non method for key ${key.toString()}`);
    }
    /**
     * Registers an array of types (constructor functions) by inspecting their registration annotations.
     *      If none are found, then the default singleton registration is used.
     * @param fns The constructor function to use when the dependency needs to be instantiated.
     */
    autoRegisterAll(fns) {
        let i = fns.length;
        while (i--) {
            this.autoRegister(fns[i]);
        }
    }
    /**
     * Unregisters based on key.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     */
    unregister(key) {
        this._resolvers.delete(key);
    }
    /**
     * Inspects the container to determine if a particular key has been registred.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @param checkParent Indicates whether or not to check the parent container hierarchy.
     * @return Returns true if the key has been registred; false otherwise.
     */
    hasHandler(key, checkParent = false) {
        validateKey(key);
        return (!!this.getResolver(key) ||
            (checkParent && this.parent != null && this.parent.hasHandler(key, checkParent)));
    }
    /**
     * Gets the resolver for the particular key, if it has been registered.
     * @param key The key that identifies the dependency at resolution time; usually a constructor function.
     * @return Returns the resolver, if registred, otherwise undefined.
     */
    getResolver(key, checkParent = false) {
        return this._getResolver(key, checkParent ? this : undefined);
    }
    /**
     * Resolves a single instance based on the provided key.
     * @param key The key that identifies the object to resolve.
     * @return Returns the resolved instance.
     */
    get(key) {
        validateKey(key);
        if (key === Container) {
            return this;
        }
        if (resolverDeco.decorates(key)) {
            return key.get(this, key);
        }
        const resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return this.autoRegister(key).get(this, key);
            }
            if (typeof key !== 'symbol' && typeof key !== 'string') {
                const registration = Reflect.getMetadata(constants.registration, key);
                if (registration) {
                    return registration.registerResolver(this, key, key).get(this, key);
                }
            }
            return this.parent._get(key, this);
        }
        return resolver.get(this, key);
    }
    /**
     * Resolves all instance registered under the provided key.
     * @param key The key that identifies the objects to resolve.
     * @return Returns an array of the resolved instances.
     */
    getAll(key) {
        validateKey(key);
        const resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return _emptyParameters;
            }
            return this.parent.getAll(key);
        }
        let results;
        if (resolver instanceof StrategyResolver && resolver.strategy === Strategy.Array) {
            const state = resolver.state;
            const len = state.length;
            let i = len;
            results = new Array(i);
            while (i--) {
                results[len - i - 1] = state[i].get(this, key);
            }
        }
        else {
            results = [resolver.get(this, key)];
        }
        return this.parent ? this.parent.getAll(key).concat(results) : results;
    }
    /**
     * Creates a new dependency injection container whose parent is the current container.
     * @return Returns a new container instance parented to this.
     */
    createChild(name) {
        const child = new Container({
            ...this._configuration,
            name: `${name || 'child'}:${containerNumber}`,
        });
        child.root = this.root;
        child.parent = this;
        return child;
    }
    /**
     * Configure the container with a given delegate method
     */
    configure(func) {
        func(this);
        return this;
    }
    /**
     * Invokes a function, recursively resolving its dependencies.
     * @param fn The function to invoke with the auto-resolved dependencies.
     * @param dynamicDependencies Additional function dependencies to use during invocation.
     * @return Returns the instance resulting from calling the function.
     */
    invoke(fn, dynamicDependencies) {
        try {
            let handler = this._handlers.get(fn);
            if (handler === undefined) {
                handler = this._createInvocationHandler(fn);
                this._handlers.set(fn, handler);
            }
            return handler.invoke(this, dynamicDependencies);
        }
        catch (e) {
            throw AggregateError(`Error invoking ${fn.name}. Check the inner error for details.`, e, true);
        }
    }
    /**
     * Invokes a function, recursively resolving its dependencies.
     * @param fn The function to invoke with the auto-resolved dependencies.
     * @param key The key being invoked.
     * @param dynamicDependencies Additional function dependencies to use during invocation.
     * @return Returns the instance resulting from calling the function.
     */
    invokeWithKey(fn, key, dynamicDependencies) {
        const value = typeof fn !== 'function' ? fn.get(this, key) : this.invoke(fn, dynamicDependencies);
        if (typeof key !== 'symbol' && typeof key !== 'string' && Reflect.hasOwnMetadata(constants.wrap, key)) {
            const wrap = Reflect.getOwnMetadata(constants.wrap, key);
            return wrap.get(value, this, key);
        }
        return value;
    }
    /**
     * Disposes of this container
     */
    dispose() {
        const resolvers = this._resolvers;
        this._resolvers.clear();
        this._resolvers = null;
        this._configuration = null;
        this.parent = null;
        this.root = null;
    }
    _getResolver(key, relativeContainer) {
        let resolver = this._resolvers.get(key);
        if (relativeContainer && !resolver && this.parent) {
            resolver = this.parent._getResolver(key, relativeContainer);
        }
        if (!this._resolvers.get(key) &&
            isStrategyResolver(resolver) &&
            resolver.strategy === Strategy.Scoped) {
            resolver = resolver.clone();
            this.registerResolver(key, resolver);
        }
        return resolver;
    }
    _get(key, relativeContainer) {
        const resolver = this.getResolver(key, true);
        if (resolver === undefined) {
            if (this.parent == null) {
                return (relativeContainer || this)
                    .autoRegister(key)
                    .get(relativeContainer || this, key);
            }
            return this.parent._get(key, relativeContainer || this);
        }
        return resolver.get(relativeContainer || this, key);
    }
    _createInvocationHandler(fn) {
        const dependencies = getInjectDependencies(fn);
        const invoker = Reflect.getOwnMetadata(constants.invoker, fn) ||
            classInvokers[dependencies.length] ||
            classInvokers.fallback;
        const handler = new InvocationHandler(fn, invoker, dependencies);
        return this._onHandlerCreated != null ? this._onHandlerCreated(handler) : handler;
    }
}
//# sourceMappingURL=data:application/json;base64,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